// =============================================================================
// hooks/sales/useCampaignWizard.ts — Chamadas de rede do wizard
// `/campanhas/nova` (F35-S08, doc 26 §6.6/§9).
//
// Fica FORA da árvore visual de features/sales/campaigns/wizard/ (mesmo
// padrão de hooks/sales/useCampaigns.ts, doc 27 §3.1) — este é o único ponto
// de acesso à rede das etapas do wizard.
//
// CRÍTICO (nota do orquestrador): a prévia (`/audience/preview`) e a
// materialização (`/audience/apply`, `/members/import`) do lado do backend
// chamam a MESMA função `classifyAudience` (F35-S03,
// apps/api/src/modules/sales/campaigns/audience/service.ts) — este hook NUNCA
// recalcula a classificação no cliente, apenas repassa o `AudienceSource`
// digitado pelo gestor e exibe o que a API devolveu. Ver aviso de escopo em
// AudiencePreviewBodySchema/AudienceApplyBodySchema
// (packages/shared-schemas/src/sales-campaigns.ts).
// =============================================================================

import type {
  AudienceApplyResponse,
  AudiencePreviewResponse,
  AudienceSource,
  Campaign,
  CampaignCreateBody,
  CampaignMembersImportResponse,
  CampaignPatchBody,
} from '@elemento/shared-schemas';
import {
  AudienceApplyResponseSchema,
  AudiencePreviewResponseSchema,
  CampaignMembersImportResponseSchema,
  CampaignSchema,
} from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from '../../lib/api';

export type {
  AudienceApplyResponse,
  AudiencePreviewResponse,
  AudienceSource,
  Campaign,
  CampaignCreateBody,
  CampaignMembersImportResponse,
  CampaignPatchBody,
};

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const campaignWizardKeys = {
  draft: (id: string) => ['sales', 'campaigns', 'wizard', 'draft', id] as const,
  membersSummary: (id: string) => ['sales', 'campaigns', 'wizard', 'members-summary', id] as const,
  importBatch: (id: string) => ['sales', 'campaigns', 'wizard', 'import-batch', id] as const,
};

// ---------------------------------------------------------------------------
// GET /api/sales/campaigns/:id — rehidrata o rascunho ao voltar para a tela
// ---------------------------------------------------------------------------

async function fetchCampaign(id: string): Promise<Campaign> {
  const raw = await api.get<unknown>(`/api/sales/campaigns/${encodeURIComponent(id)}`);
  return CampaignSchema.parse(raw);
}

export function useCampaignDraft(
  campaignId: string | null,
  opts: { enabled?: boolean } = {},
): {
  data: Campaign | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  const enabled = (opts.enabled ?? true) && campaignId !== null;
  const { data, isLoading, isError, error } = useQuery({
    queryKey: campaignWizardKeys.draft(campaignId ?? 'none'),
    queryFn: () => fetchCampaign(campaignId as string),
    enabled,
    retry: false,
  });

  return { data, isLoading: enabled && isLoading, isError, error: error as Error | null };
}

// ---------------------------------------------------------------------------
// POST /api/sales/campaigns — cria o rascunho (etapa 1, 1ª vez)
// ---------------------------------------------------------------------------

async function createCampaign(body: CampaignCreateBody): Promise<Campaign> {
  const raw = await api.post<unknown>('/api/sales/campaigns', body);
  return CampaignSchema.parse(raw);
}

export function useCreateCampaignDraft(): {
  mutateAsync: (body: CampaignCreateBody) => Promise<Campaign>;
  isPending: boolean;
} {
  const { mutateAsync, isPending } = useMutation({ mutationFn: createCampaign });
  return { mutateAsync, isPending };
}

// ---------------------------------------------------------------------------
// PATCH /api/sales/campaigns/:id — salva cada etapa seguinte (idempotente)
// ---------------------------------------------------------------------------

async function patchCampaign(id: string, body: CampaignPatchBody): Promise<Campaign> {
  const raw = await api.patch<unknown>(`/api/sales/campaigns/${encodeURIComponent(id)}`, body);
  return CampaignSchema.parse(raw);
}

export function usePatchCampaignDraft(): {
  mutateAsync: (args: { id: string; body: CampaignPatchBody }) => Promise<Campaign>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CampaignPatchBody }) => patchCampaign(id, body),
    onSuccess: (campaign) => {
      queryClient.setQueryData(campaignWizardKeys.draft(campaign.id), campaign);
    },
  });
  return { mutateAsync, isPending };
}

// ---------------------------------------------------------------------------
// POST /api/sales/campaigns/:id/audience/preview — NUNCA grava (etapa 3)
// ---------------------------------------------------------------------------

async function previewAudience(
  id: string,
  source: AudienceSource,
): Promise<AudiencePreviewResponse> {
  const raw = await api.post<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(id)}/audience/preview`,
    source,
  );
  return AudiencePreviewResponseSchema.parse(raw);
}

export function usePreviewAudience(): {
  mutateAsync: (args: { id: string; source: AudienceSource }) => Promise<AudiencePreviewResponse>;
  isPending: boolean;
} {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, source }: { id: string; source: AudienceSource }) =>
      previewAudience(id, source),
  });
  return { mutateAsync, isPending };
}

// ---------------------------------------------------------------------------
// POST /api/sales/campaigns/:id/audience/apply — materializa (etapa 3 →  4)
// ---------------------------------------------------------------------------

async function applyAudience(id: string, source: AudienceSource): Promise<AudienceApplyResponse> {
  const raw = await api.post<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(id)}/audience/apply`,
    source,
  );
  return AudienceApplyResponseSchema.parse(raw);
}

export function useApplyAudience(): {
  mutateAsync: (args: { id: string; source: AudienceSource }) => Promise<AudienceApplyResponse>;
  isPending: boolean;
} {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, source }: { id: string; source: AudienceSource }) =>
      applyAudience(id, source),
  });
  return { mutateAsync, isPending };
}

// ---------------------------------------------------------------------------
// GET /api/imports/:id — verifica um lote de importação antes de aplicá-lo
// ---------------------------------------------------------------------------

const ImportBatchSummarySchema = z.object({
  id: z.string().uuid(),
  entityType: z.string(),
  fileName: z.string(),
  status: z.string(),
  totalRows: z.number().int(),
  validRows: z.number().int(),
  invalidRows: z.number().int(),
});
export type ImportBatchSummary = z.infer<typeof ImportBatchSummarySchema>;

async function fetchImportBatch(id: string): Promise<ImportBatchSummary> {
  const raw = await api.get<unknown>(`/api/imports/${encodeURIComponent(id)}`);
  return ImportBatchSummarySchema.parse(raw);
}

export function useImportBatch(batchId: string | null): {
  data: ImportBatchSummary | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const enabled = batchId !== null && batchId.length > 0;
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: campaignWizardKeys.importBatch(batchId ?? 'none'),
    queryFn: () => fetchImportBatch(batchId as string),
    enabled,
    retry: false,
  });

  return { data, isLoading: enabled && isLoading, isError, error: error as Error | null, refetch };
}

// ---------------------------------------------------------------------------
// POST /api/sales/campaigns/:id/members/import — materializa a partir de um
// lote já concluído (etapa 3, fonte "arquivo importado"). Ao contrário de
// preview/apply, este endpoint GRAVA diretamente — não existe prévia
// não-destrutiva para esta fonte (ver nota de escopo em Step3Hygiene.tsx).
// ---------------------------------------------------------------------------

async function importCampaignMembers(
  id: string,
  importBatchId: string,
): Promise<CampaignMembersImportResponse> {
  const raw = await api.post<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(id)}/members/import`,
    { importBatchId },
  );
  return CampaignMembersImportResponseSchema.parse(raw);
}

export function useImportCampaignMembers(): {
  mutateAsync: (args: {
    id: string;
    importBatchId: string;
  }) => Promise<CampaignMembersImportResponse>;
  isPending: boolean;
} {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, importBatchId }: { id: string; importBatchId: string }) =>
      importCampaignMembers(id, importBatchId),
  });
  return { mutateAsync, isPending };
}

// ---------------------------------------------------------------------------
// GET /api/sales/campaigns/:id/members — resumo p/ a confirmação de ativação
// (etapa 6: "quantas pessoas entram na fila", DoD — nunca genérica).
// ---------------------------------------------------------------------------

const MembersSummaryResponseSchema = z.object({
  total: z.number().int(),
});

export interface MembersSummary {
  total: number;
  blocked: number;
}

async function fetchMembersSummary(id: string): Promise<MembersSummary> {
  const base = `/api/sales/campaigns/${encodeURIComponent(id)}/members`;
  const [allRaw, blockedRaw] = await Promise.all([
    api.get<unknown>(`${base}?limit=1`),
    api.get<unknown>(`${base}?limit=1&status=blocked`),
  ]);
  return {
    total: MembersSummaryResponseSchema.parse(allRaw).total,
    blocked: MembersSummaryResponseSchema.parse(blockedRaw).total,
  };
}

export function useMembersSummary(
  campaignId: string | null,
  opts: { enabled?: boolean } = {},
): {
  data: MembersSummary | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const enabled = (opts.enabled ?? true) && campaignId !== null;
  const { data, isLoading, isError } = useQuery({
    queryKey: campaignWizardKeys.membersSummary(campaignId ?? 'none'),
    queryFn: () => fetchMembersSummary(campaignId as string),
    enabled,
  });

  return { data, isLoading: enabled && isLoading, isError };
}

// ---------------------------------------------------------------------------
// POST /api/sales/campaigns/:id/activate — ação separada e auditada (etapa 6)
// ---------------------------------------------------------------------------

async function activateCampaign(id: string): Promise<Campaign> {
  const raw = await api.post<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(id)}/activate`,
    {},
  );
  return CampaignSchema.parse(raw);
}

export function useActivateCampaign(): {
  mutateAsync: (id: string) => Promise<Campaign>;
  isPending: boolean;
} {
  const { mutateAsync, isPending } = useMutation({ mutationFn: activateCampaign });
  return { mutateAsync, isPending };
}
